// X&O (Tic-Tac-Toe) page — server-authoritative.
//
// The board, turn state, round progression, match result, timeout
// handling, bot AI, wallet debit/credit all live on the server
// (XoEngine + XoRoom). This page is a presentation layer that:
//   - connects to /api/xo/ws
//   - sends `xo_join_queue` / `xo_place_mark` / `xo_leave_game`
//   - renders UI state from server broadcasts:
//       xo_match_start, xo_mark_placed, xo_turn_update,
//       xo_round_end, xo_round_start, xo_match_over,
//       xo_player_kicked, xo_reconnect_sync, xo_waiting_in_queue,
//       wallet_update, voice_chunk, rtc_signal, xo_global_stats
//
// The turn timer rendered here is PURELY VISUAL; the authoritative
// timeout fires on the server and broadcasts `xo_turn_timeout` +
// `xo_round_end`.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import Icon from '@baaligames/shared-ui/components/Icon';
import InsufficientFundsModal from '@baaligames/shared-ui/components/InsufficientFundsModal';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import LobbyBanner from '@baaligames/shared-ui/components/LobbyBanner';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader, flattenImageList } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { isSessionAcked, markSessionAcked, clearSessionAcked } from '@baaligames/shared-ui/lib/recoveryAck';
import Board, { Cell } from './Board';
import Mark from './Mark';
import { XoWS, type WSStatus } from './ws';
import { api } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance } from '@baaligames/shared-ui/lib/wallet';
// ~~ Per-game WSVoice (see comment in xo/wsVoice.ts for why this cannot
// be shared with Ludo — each game MUST have its own `__<game>MicStream`
// key or the stream handoff between the Play-button gesture and the
// start() call misses, and browsers silently refuse or degrade the mic.
import { WSVoice } from './wsVoice';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import {
  playTap,
  playJumpSound,
  playSlotLock,
  playWinFanfare,
  playLossTone,
  playCancelSound,
  startUrgencyBeep,
  stopUrgencyBeep,
  setSoundEnabled as setSfxEnabled,
} from '../ludo/sounds';
import '../ludo/ludo.css';   // shared shell (app-container, etc.)
import './xo.css';

// Image set for the X&O UI. Currently mirrors Ludo's art as
// placeholders — files live at `backend/public/games/xo/*` so they
// can be swapped one-by-one with real X&O artwork without touching
// any code. Paths below are relative to the backend's public root
// (served at `/games/xo/...`).
const IMG = {
  logo:        '/games/xo/Logo.png',
  coin:        '/games/xo/Coin.png',
  cash:        ['/games/xo/Cash-1.png', '/games/xo/Cash-2.png', '/games/xo/Cash-3.png', '/games/xo/Cash-4.png'],
  playerX:     '/games/xo/RC_Player_blue.png',
  playerO:     '/games/xo/RC_Player_red.png',
  trophy:      '/games/xo/Victory_Trophy.png',
  defeat:      '/games/xo/Defeat_Medal.png',
  quitWarn:    '/games/xo/Quit_Warning.png',
  settings:    '/games/xo/Settings.png',
  plus:        '/games/xo/Plus.png',
};

// Stable preload list — module-scoped so identity is fixed across
// renders, otherwise useAssetPreloader would restart on every render.
const XO_PRELOAD_URLS = flattenImageList(IMG);

type Phase = 'lobby' | 'queue' | 'vs' | 'playing' | 'between' | 'result';
type Mark = 'x' | 'o';

// Bet tile defaults — used ONLY until `/api/xo/config` resolves on
// mount. The server is the source of truth; any admin change to
// `BET_TIERS` or `PLATFORM_FEE_PERCENT` via /api/admin/config/xo/...
// propagates through the fetched `config` state, not these constants.
// (Previously these were the hard-coded UI values and admin edits to
// the DB never reached the lobby — classic frontend/backend drift.)
const BET_TIERS_FALLBACK = [10, 50, 100, 250];
const PLATFORM_FEE_FALLBACK = 0; // platform fee ABOLISHED (H5/H7)

// Display-only copy of MAX_ROUNDS (server is authoritative via
// xo_match_start's `max_rounds`). 3 matches the default xoControl.js
// value; UI strings that reference "of 3" use this.
const MAX_ROUNDS = 3;

// Visual turn-timer defaults. The server is the source of truth via
// `XoGame.TURN_SECONDS` + `CLIENT_TIMER_OFFSET_SECONDS` + `URGENCY_BEEP_LEAD_SECONDS`
// — these constants are ONLY the fallback used until /api/xo/config
// resolves on mount. Admin edits propagate through the fetched state
// below; never reference these constants directly once config loads.
const TURN_SECONDS_FALLBACK            = 15;
const CLIENT_TIMER_OFFSET_FALLBACK     = 1;
const URGENCY_BEEP_LEAD_FALLBACK       = 3;

// Note: winning lines live on the server (XoGame.WIN_LINES +
// XoEngine.detectWinner). The client just renders the `line` that
// comes in the `xo_round_end` broadcast.

const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

type Profile = { name?: string; avatarUrl?: string | null; isBot?: boolean };

export default function Xo() {
  const nav = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  // ─── Bootstrap gate: assets + WS auth ─────────────────────────────
  // The loading overlay covers the page until both signals turn green.
  // `assetStatus.ready` flips when every preloaded image has settled;
  // `authReady` flips when the server's xo_auth_success lands.
  const assetStatus = useAssetPreloader(XO_PRELOAD_URLS, 600);
  const [authReady, setAuthReady] = useState(false);

  // ─── Phase machine ────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('lobby');
  const [selectedBet, setSelectedBet] = useState<number>(BET_TIERS_FALLBACK[0]);
  const [, setWsStatus] = useState<WSStatus>('connecting');

  // ─── Admin-editable server config (hot-reloaded on every mount) ────
  // Fetched from GET /api/xo/config — same pattern Ludo/Rummy/Chess
  // use. Keeps the bet tiles + fee in sync with whatever the admin
  // set in `/api/admin/config/xo/...` without a redeploy.
  const [betTiers, setBetTiers] = useState<number[]>(BET_TIERS_FALLBACK);
  const [platformFeePercent, setPlatformFeePercent] = useState<number>(PLATFORM_FEE_FALLBACK);
  const [turnSeconds, setTurnSeconds] = useState<number>(TURN_SECONDS_FALLBACK);
  const [clientTimerOffsetSeconds, setClientTimerOffsetSeconds] =
    useState<number>(CLIENT_TIMER_OFFSET_FALLBACK);
  const [urgencyBeepLeadSeconds, setUrgencyBeepLeadSeconds] =
    useState<number>(URGENCY_BEEP_LEAD_FALLBACK);
  // Platform-wide announcement banner (admin-controlled).
  const [lobbyBanner, setLobbyBanner] =
    useState<{ enabled: boolean; text: string; tone: 'info' | 'warning' | 'critical' }>(
      { enabled: false, text: '', tone: 'info' });
  // Concurrent-match modal state.
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [concOpen,     setConcOpen]     = useState(false);

  // ─── Settings / voice controls ────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  useEffect(() => { setSfxEnabled(soundEnabled); }, [soundEnabled]);

  // ─── Identity / opponent ──────────────────────────────────────────
  // yourMark arrives in xo_match_start / xo_reconnect_sync; we pick
  // nothing locally. The server-side Fisher-Yates in XoRoom ensures
  // random X/O assignment per match.
  const [yourMark, setYourMark] = useState<Mark | null>(null);
  const [profiles, setProfiles] = useState<Record<Mark, Profile>>({
    x: { name: 'X', avatarUrl: null, isBot: false },
    o: { name: 'O', avatarUrl: null, isBot: false },
  });
  const youName = (yourMark && profiles[yourMark]?.name) || user?.username || 'You';
  const oppMark: Mark | null = yourMark === 'x' ? 'o' : yourMark === 'o' ? 'x' : null;
  const oppName = (oppMark && profiles[oppMark]?.name) || 'Opponent';

  // ─── Match state (all server-sourced) ─────────────────────────────
  const [cells, setCells] = useState<Cell[]>(Array(9).fill(null));
  const [currentTurn, setCurrentTurn] = useState<Mark>('x');
  const [round, setRound] = useState<number>(1);
  const [score, setScore] = useState<{ x: number; o: number; draws: number }>({ x: 0, o: 0, draws: 0 });
  // Per-mark timeout strikes accumulated this match. Resets on
  // match_start / reconnect_sync, ticks up on xo_turn_timeout.
  const [strikes, setStrikes] = useState<{ x: number; o: number }>({ x: 0, o: 0 });
  // Seat whose WebSocket dropped mid-match. Not forfeited yet (see
  // XoGame.DISCONNECT_GRACE_SECONDS); we just grey the opponent panel,
  // drop their voice peer, and pause the local turn timer.
  const [absent, setAbsent] = useState<Mark | null>(null);
  // Per-stake queue badge state. `waitingByBet[bet]` = real humans
  // currently queued at that stake; `showWaitingBadge` is the admin
  // toggle from XoGame.SHOW_QUEUE_WAITING_BADGE — false hides all
  // badges even while the counts themselves keep updating.
  const [waitingByBet, setWaitingByBet] = useState<Record<string, number>>({});
  const [showWaitingBadge, setShowWaitingBadge] = useState<boolean>(false);
  // Server-driven online count — same pattern as Ludo. The server's
  // drift tick (backend/src/games/xo/room.js) walks a base number up
  // and down slowly while bots are enabled; the client renders the
  // number so the lobby feels alive rather than empty during off-peak
  // hours. Starts at 0 until the first xo_global_stats arrives.
  const [online, setOnline] = useState<number>(0);
  const [lastRoundWinner, setLastRoundWinner] = useState<Mark | 'draw' | null>(null);
  const [winLine, setWinLine] = useState<number[] | null>(null);
  const [turnSeq, setTurnSeq] = useState(0);
  const [matchPot, setMatchPot] = useState<number>(0);
  // 2026-05-19 — matchPayout is DERIVED on every render from matchPot
  // and the CURRENT platformFeePercent. It used to be state, set inside
  // the WS message handlers — which closed over the FALLBACK value of
  // platformFeePercent (25) before the /api/xo/config fetch landed and
  // updated state to the live value (e.g. 20). The result: in-match
  // top bar and victory modal kept showing the 25%-fee payout even
  // after the lobby (which computes inline) had switched to the live
  // 20%-fee payout. Deriving inline eliminates the stale closure.
  const matchPayout = Math.floor((matchPot || 0) * (100 - platformFeePercent) / 100);
  const [matchOutcome, setMatchOutcome] = useState<'win' | 'loss' | 'draw' | null>(null);
  // Live queue snapshot — populated from xo_waiting_in_queue events.
  // Lets the waiting modal show the opponent's actual avatar + name
  // the instant they're seated by the server, instead of the generic
  // 4-color placeholder cycle that was the only visual before.
  const [queueProfiles, setQueueProfiles] = useState<{
    profiles: Array<{ userId?: string; name?: string; displayName?: string; avatarUrl?: string | null; isBot?: boolean; is_bot?: boolean }>;
    yourUserId: string | null;
  }>({ profiles: [], yourUserId: null });
  const [wallet, setWallet] = useState<number>(0);
  // Insufficient-funds prompt.
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientBet, setInsufficientBet] = useState<number>(0);
  // Quit-confirm modal shown when user taps Back or Exit mid-match.
  const [quitConfirm, setQuitConfirm] = useState(false);
  // Previous-game recovery popup (served from /api/xo/pending-result).
  const [recovery, setRecovery] = useState<any | null>(null);

  // Refs for stable WS callbacks.
  const wsRef = useRef<XoWS | null>(null);
  const voiceRef = useRef<WSVoice | null>(null);
  const urgencyTimerRef = useRef<number | null>(null);
  const vsHoldTimerRef = useRef<number | null>(null);
  const yourMarkRef = useRef<Mark | null>(null);
  const phaseRef = useRef<Phase>('lobby');
  useEffect(() => { yourMarkRef.current = yourMark; }, [yourMark]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ─── Hide app chrome + unlock audio ───────────────────────────────
  useEffect(() => {
    document.body.classList.add('ludo-active');
    return () => document.body.classList.remove('ludo-active');
  }, []);

  // ─── WebSocket connection + message handler ───────────────────────
  useEffect(() => {
    let closed = false;
    (async () => {
      // Load wallet + pending recovery. If the user has a live session
      // on the server, `xo_reconnect_sync` will arrive after WS auth
      // and override the local 'lobby' default with the right state.
      // Fetch the admin-tunable server config (bet tiers, platform
      // fee, etc.). Silent-fail to the hard-coded fallbacks so the
      // lobby is still usable if this single endpoint blips.
      try {
        const cfg = await api.get<{
          betTiers?: number[];
          platformFeePercent?: number;
          turnSeconds?: number;
          clientTimerOffsetSeconds?: number;
          urgencyBeepLeadSeconds?: number;
          lobbyBanner?: { enabled: boolean; text: string; tone: 'info' | 'warning' | 'critical' };
        }>('/xo/config');
        if (!closed && cfg) {
          if (Array.isArray(cfg.betTiers) && cfg.betTiers.length) {
            setBetTiers(cfg.betTiers);
            // Keep `selectedBet` on a legal tile — if the admin
            // removed the previously-selected tier we reset to the
            // first one from the new list.
            setSelectedBet((cur) =>
              cfg.betTiers!.includes(cur) ? cur : cfg.betTiers![0]);
          }
          if (typeof cfg.platformFeePercent === 'number') {
            setPlatformFeePercent(cfg.platformFeePercent);
          }
          // Wire timing knobs through so admin edits immediately
          // change the turn-bar drain duration and the urgency-beep
          // lead time on the client.
          if (typeof cfg.turnSeconds === 'number' && cfg.turnSeconds > 0) {
            setTurnSeconds(cfg.turnSeconds);
          }
          if (typeof cfg.clientTimerOffsetSeconds === 'number') {
            setClientTimerOffsetSeconds(cfg.clientTimerOffsetSeconds);
          }
          if (typeof cfg.urgencyBeepLeadSeconds === 'number') {
            setUrgencyBeepLeadSeconds(cfg.urgencyBeepLeadSeconds);
          }
          if (cfg.lobbyBanner && typeof cfg.lobbyBanner === 'object') {
            setLobbyBanner({
              enabled: !!cfg.lobbyBanner.enabled,
              text:    String(cfg.lobbyBanner.text || ''),
              tone:    (cfg.lobbyBanner.tone === 'warning' || cfg.lobbyBanner.tone === 'critical')
                       ? cfg.lobbyBanner.tone : 'info',
            });
          }
        }
      } catch {}
      try {
        const w = await api.get<{ balance: number }>('/xo/wallet');
        if (!closed) {
          setWallet(w.balance);
          setWalletBalance(w.balance); // keep dashboard chip in sync
        }
      } catch {}
      // Recovery card — show the previous match's outcome if the user
      // never acknowledged it. Only surface in the lobby; the WS
      // reconnect_sync path will override if an active session is
      // still in progress (race-guarded by phaseRef just like Ludo).
      try {
        const pr = await api.get<{ pending: any }>('/xo/pending-result');
        if (!closed && pr?.pending) {
          // If the user already dismissed this session on a previous
          // visit but the server missed the acknowledge POST, retry
          // silently and DON'T re-show the popup. See core/lib/recoveryAck.
          if (isSessionAcked('xo', pr.pending.sessionId)) {
            api.post('/xo/acknowledge-result', { sessionId: pr.pending.sessionId })
              .then(() => clearSessionAcked('xo', pr.pending.sessionId))
              .catch(() => {});
          } else {
            setTimeout(() => {
              if (closed) return;
              if (phaseRef.current !== 'lobby') return;
              setRecovery(pr.pending);
            }, 1200);
          }
        }
      } catch {}
      const ws = new XoWS();
      ws.on(onMessage);
      ws.onStatus(setWsStatus);
      ws.connect();
      wsRef.current = ws;
    })();
    return () => {
      closed = true;
      wsRef.current?.close();
      wsRef.current = null;
      voiceRef.current?.stop();
      voiceRef.current = null;
      if (urgencyTimerRef.current) { clearTimeout(urgencyTimerRef.current); urgencyTimerRef.current = null; }
      if (vsHoldTimerRef.current)  { clearTimeout(vsHoldTimerRef.current);  vsHoldTimerRef.current = null; }
      stopUrgencyBeep();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onMessage(msg: any) {
    switch (msg.type) {
      case 'xo_auth_success':
        // Server confirms auth; flip authReady so the loading overlay
        // can release once asset preload has also completed. Any
        // active session triggers xo_reconnect_sync next.
        setAuthReady(true);
        break;

      case 'xo_you_are':
        // Per-recipient message sent immediately before xo_match_start
        // so yourMark is authoritative (no display-name guessing).
        setYourMark(msg.mark);
        yourMarkRef.current = msg.mark;
        break;

      case 'xo_waiting_in_queue':
        // Server emits this every time the queue's profile set changes
        // (us joining, opponent joining, opponent leaving). Capture
        // the profile array + our own id so the waiting modal can
        // hot-swap from the "searching..." placeholder to the real
        // opponent's avatar + name the moment they're seated. Pre-fix
        // we discarded msg entirely and the modal was stuck on the
        // generic colored placeholder for the whole wait.
        setQueueProfiles({
          profiles: Array.isArray(msg.profiles) ? msg.profiles : [],
          // Server emits the local-player id in snake_case
          // (`your_user_id`). Plan D originally read camelCase
          // (`yourUserId`), which left this null and made the
          // opponent-find lambda in the modal match every profile —
          // surfacing the local player as "OPPONENT" with their own
          // avatar+name. Read the snake-case form first; keep camelCase
          // as a defensive fallback.
          yourUserId: (msg.your_user_id || msg.yourUserId)
            ? String(msg.your_user_id || msg.yourUserId)
            : null,
        });
        setPhase('queue');
        break;

      case 'xo_match_start': {
        // Kick off voice chat alongside the match. Reuses Ludo's
        // WSVoice class verbatim; we cast the mark string into the
        // Color slot because the class only compares it as a string
        // internally (skip-self key).
        startVoice();
        // msg: { session_id, players, playerProfiles, turn, round, pot, max_rounds, rounds_to_win }
        const profs = (msg.playerProfiles || {}) as Record<Mark, any>;
        setProfiles({
          x: { name: profs.x?.displayName || 'X', avatarUrl: profs.x?.avatarUrl, isBot: !!profs.x?.isBot },
          o: { name: profs.o?.displayName || 'O', avatarUrl: profs.o?.avatarUrl, isBot: !!profs.o?.isBot },
        });
        setMatchPot(msg.pot || 0);
        // matchPayout is derived from matchPot (see top of component) — no setter needed.
        setCells(Array(9).fill(null));
        setCurrentTurn(msg.turn || 'x');
        setRound(msg.round || 1);
        setScore({ x: 0, o: 0, draws: 0 });
        setStrikes({ x: 0, o: 0 });
        setLastRoundWinner(null);
        setWinLine(null);
        setTurnSeq((s) => s + 1);
        setPhase('vs');
        // 3s VS intro — identical cadence to Ludo.
        if (vsHoldTimerRef.current) clearTimeout(vsHoldTimerRef.current);
        vsHoldTimerRef.current = window.setTimeout(() => setPhase('playing'), 3000);
        break;
      }

      case 'xo_reconnect_sync': {
        // msg: { your_mark, playerProfiles, cells, turn, round, score, strikes, pot, status, ... }
        const profs = (msg.playerProfiles || {}) as Record<Mark, any>;
        setProfiles({
          x: { name: profs.x?.displayName || 'X', avatarUrl: profs.x?.avatarUrl, isBot: !!profs.x?.isBot },
          o: { name: profs.o?.displayName || 'O', avatarUrl: profs.o?.avatarUrl, isBot: !!profs.o?.isBot },
        });
        setYourMark(msg.your_mark);
        yourMarkRef.current = msg.your_mark;
        setCells(Array.isArray(msg.cells) ? msg.cells.slice() : Array(9).fill(null));
        setCurrentTurn(msg.turn || 'x');
        setRound(msg.round || 1);
        setScore(msg.score || { x: 0, o: 0, draws: 0 });
        setStrikes(msg.strikes || { x: 0, o: 0 });
        setMatchPot(msg.pot || 0);
        // matchPayout is derived from matchPot (see top of component) — no setter needed.
        setLastRoundWinner(null);
        setWinLine(null);
        setTurnSeq((s) => s + 1);
        setMatchOutcome(null);
        setRecovery(null); // live session beats any stale recovery popup
        setPhase(msg.status === 'finished' ? 'result' : 'playing');
        break;
      }

      case 'xo_mark_placed': {
        // msg: { player, index, cells }
        const next: Cell[] = Array.isArray(msg.cells) ? msg.cells.slice() : cells.slice();
        setCells(next);
        playJumpSound();
        break;
      }

      case 'xo_turn_update':
        setCurrentTurn(msg.turn);
        setTurnSeq((s) => s + 1);
        break;

      case 'xo_turn_timeout':
        // Strike tick arrived. `xo_round_end` right after covers the
        // score change; here we update the per-mark strike counter
        // so the player card's strike-dot indicator lights up.
        if (msg.player && typeof msg.strikes === 'number') {
          setStrikes((s) => ({ ...s, [msg.player]: msg.strikes }));
        }
        break;

      case 'xo_round_end': {
        // msg: { round, winner, line, cells, score, timeout }
        const w: Mark | null = msg.winner || null;
        if (Array.isArray(msg.cells)) setCells(msg.cells.slice());
        setWinLine(Array.isArray(msg.line) ? msg.line : null);
        setLastRoundWinner(w || 'draw');
        if (msg.score) setScore(msg.score);
        setPhase('between');
        const me = yourMarkRef.current;
        if (me && w === me) playSlotLock();
        else if (me && w && w !== me) playCancelSound();
        stopUrgencyBeep();
        break;
      }

      case 'xo_round_start': {
        // msg: { round, starter, cells, score }
        setCells(Array.isArray(msg.cells) ? msg.cells.slice() : Array(9).fill(null));
        setCurrentTurn(msg.starter || 'x');
        setRound(msg.round || 1);
        if (msg.score) setScore(msg.score);
        setLastRoundWinner(null);
        setWinLine(null);
        setTurnSeq((s) => s + 1);
        setPhase('playing');
        break;
      }

      case 'xo_match_over': {
        // Tear down voice before the result screen shows.
        voiceRef.current?.stop();
        voiceRef.current = null;
        // msg: { winner, winnerName, payout, pot, draw, score }
        const me = yourMarkRef.current;
        if (msg.draw) setMatchOutcome('draw');
        else if (me && msg.winner === me) { setMatchOutcome('win'); playWinFanfare(); }
        else if (me) { setMatchOutcome('loss'); playLossTone(); }
        if (msg.score) setScore(msg.score);
        stopUrgencyBeep();
        setPhase('result');
        break;
      }

      case 'xo_player_kicked':
        // Other player was kicked (strike limit / manual exit). The
        // xo_match_over that follows covers the UX transition. Clear
        // any lingering "absent" grey from a preceding disconnect
        // announcement so the result screen doesn't render the seat
        // as both absent AND kicked.
        setAbsent(null);
        break;

      case 'xo_player_disconnected':
        // Opponent's WebSocket dropped. The server has paused the turn
        // clock and will auto-forfeit if the grace window expires. In
        // the meantime, drop their voice peer (so audio doesn't keep
        // a dead slot) and mark them absent so the panel reads "away".
        if (msg.player && msg.player !== yourMark) {
          voiceRef.current?.dropPeer?.(msg.player);
        }
        if (msg.player) setAbsent(msg.player);
        break;

      case 'xo_player_reconnected':
        // Opponent came back before the grace timer expired. Undo the
        // absent flag; their own client re-opens the WSVoice channel,
        // so we don't need to explicitly re-subscribe here.
        if (msg.player && absent === msg.player) setAbsent(null);
        break;

      case 'wallet_update':
        if (typeof msg.balance === 'number') {
          // Local state for the in-game TopBar chip + shared store
          // for the dashboard NavBar chip / Wallet page so every
          // mounted wallet view stays in sync without a refresh.
          setWallet(msg.balance);
          setWalletBalance(msg.balance);
        }
        break;

      case 'xo_left_game_success':
        // Confirmation after a leave; the phase transition was already
        // optimistic on the client.
        break;

      case 'xo_global_stats':
        // Server-driven lobby telemetry — online count (drift-driven
        // by BOT_ONLINE_BASE + BOT_ONLINE_DRIFT on the backend), plus
        // the per-stake waiting badge counts and the admin-toggle
        // flag that hides them.
        if (typeof msg.online_players === 'number') {
          setOnline(msg.online_players);
        }
        if (typeof msg.show_waiting_badge === 'boolean') {
          setShowWaitingBadge(msg.show_waiting_badge);
        }
        if (msg.waiting_by_bet && typeof msg.waiting_by_bet === 'object') {
          setWaitingByBet(msg.waiting_by_bet);
        }
        break;

      case 'voice_chunk':
        if (msg.from && typeof msg.pcm === 'string') {
          voiceRef.current?.onRemoteChunk(msg.from as any, msg.pcm);
        }
        break;
      case 'rtc_signal':
        // Reserved for future WebRTC-based voice. The WS relay path
        // (voice_chunk) is the only one we actually use today; the
        // rtc_signal channel exists so a future WebRTC mode can slot
        // in without changing the WS routing.
        break;

      case 'error':
        // Belt-and-braces fallback. Pre-flight wallet check in
        // startMatch handles the common case; this covers the race
        // where wallet was enough client-side but moved below the bet
        // between the check and the server processing.
        if (msg.code === 'insufficient_funds') {
          setInsufficientOpen(true);
          setPhase('lobby');
        } else if (msg.code === 'concurrent_match_limit') {
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          setPhase('lobby');
        } else if (msg.code === 'daily_loss_cap') {
          toast.push('error', 'You\'ve reached your daily loss limit. Try again tomorrow.');
          setPhase('lobby');
        } else if (msg.code === 'bet_limit') {
          toast.push('error', msg.message || 'This bet is above your limit — contact your agent.');
          setPhase('lobby');
        }
        break;
    }
  }

  // ─── Turn-timer visual (non-authoritative) ────────────────────────
  // The server is the source of truth on whether a timeout actually
  // happens (xo_round_end with timeout:true). The bar here exists
  // purely so the user feels the pressure; the urgency beep fires
  // on the final URGENCY_BEEP_LEAD_SECONDS (admin-tunable).
  useEffect(() => {
    if (phase !== 'playing') return;
    stopUrgencyBeep();
    // Both timers now use the admin-controlled `turnSeconds` +
    // `urgencyBeepLeadSeconds` instead of hard-coded 15+3.
    const urgencyAt = Math.max(0, (turnSeconds - urgencyBeepLeadSeconds) * 1000);
    urgencyTimerRef.current = window.setTimeout(() => startUrgencyBeep(), urgencyAt);
    // Hard stop at bar end so the beep can't outlive the visible bar.
    const stopAt = turnSeconds * 1000;
    const stopT = window.setTimeout(() => stopUrgencyBeep(), stopAt);
    return () => {
      if (urgencyTimerRef.current) { clearTimeout(urgencyTimerRef.current); urgencyTimerRef.current = null; }
      clearTimeout(stopT);
      stopUrgencyBeep();
    };
  }, [phase, turnSeq, turnSeconds, urgencyBeepLeadSeconds]);

  // "Searching for opponent" heartbeat tap while in queue.
  useEffect(() => {
    if (phase !== 'queue') return;
    const t = window.setInterval(() => playTap(), 1400);
    return () => clearInterval(t);
  }, [phase]);

  // ─── Actions ──────────────────────────────────────────────────────
  function startMatch(bet: number) {
    // Pre-flight balance check. The server will also reject via
    // `insufficient_funds` but this gives instant feedback without a
    // WS round-trip + without the optimistic queue screen flicker.
    if (bet > 0 && wallet < bet) {
      setInsufficientBet(bet);
      setInsufficientOpen(true);
      return;
    }
    setSelectedBet(bet);
    setMatchPot(bet * 2);
    // matchPayout is derived from matchPot (see top of component) — no setter needed.
    setScore({ x: 0, o: 0, draws: 0 });
    setRound(1);
    setLastRoundWinner(null);
    setWinLine(null);
    setCells(Array(9).fill(null));
    setInsufficientBet(bet);    // remember for the error-code fallback
    // Pre-warm mic permission inside the user click so iOS/Safari
    // grants it. Stashed on window for startVoice() to pick up when
    // the match actually starts.
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => { (window as any).__xoMicStream = stream; })
        .catch((e) => { console.warn('[xo voice] mic permission declined', e); });
    }
    setPhase('queue');
    wsRef.current?.send({ type: 'xo_join_queue', bet });
  }

  function handleCancelQueue() {
    wsRef.current?.send({ type: 'xo_leave_game' });
    // Refund is processed server-side; the wallet_update arrives via WS.
    setPhase('lobby');
  }

  // Voice-chat lifecycle. Starts when a match begins; stops on
  // match_over / unmount. Honors the current mic/listen state so
  // reopening the settings menu doesn't resurrect muted audio.
  //
  // Rewritten to match the Ludo / Chess / Rummy pattern exactly. The
  // previous version had two bugs:
  //
  //   1. It imported WSVoice from `../ludo/wsVoice`, which reads
  //      `window.__ludoMicStream` — but this page stashes the stream
  //      under `window.__xoMicStream`. The warm-stream handoff missed
  //      every single time, forcing `getUserMedia()` to re-run outside
  //      the Play-button gesture. iOS Safari + strict mobile Chromes
  //      silently refused, so voice was effectively dead. When it did
  //      work, it came up 10–20 s late as the browser's own permission
  //      retry eventually resolved — matching the "terrible lag"
  //      symptom the user reported. The fix is a dedicated
  //      `./wsVoice` with the right key.
  //   2. It called BOTH `attachLocalStream(stream)` and then `start()`
  //      back-to-back. `attachLocalStream` itself already calls
  //      `start()` internally, so capture pipelines were duplicated
  //      — two AudioContexts, two ScriptProcessors, two streams of
  //      frames flying over the wire. That caused the audible
  //      "breakage" / double-speaker artefact on top of the lag.
  function startVoice() {
    if (!yourMarkRef.current) return;
    voiceRef.current?.stop();
    voiceRef.current = null;
    const v = new WSVoice(
      yourMarkRef.current as any, // 'x' | 'o' as the own-id key
      (msg) => { wsRef.current?.send(msg); },
    );
    if (micMuted)    v.setMuted(true);
    if (listenMuted) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((ok) => {
      if (ok) {
        console.info('[xo/voice] started as mark', yourMarkRef.current);
        toast.push('success', 'Voice chat enabled', 1800);
      } else {
        toast.push('error', 'Mic blocked — check browser permissions');
      }
    });
  }

  function handleCellClick(i: number) {
    if (phase !== 'playing') return;
    if (!yourMark || currentTurn !== yourMark) return;
    if (cells[i] != null) return;
    playTap();
    wsRef.current?.send({ type: 'xo_place_mark', index: i });
  }

  function handleBackToLobby() {
    setMatchOutcome(null);
    setScore({ x: 0, o: 0, draws: 0 });
    setRound(1);
    setCells(Array(9).fill(null));
    setLastRoundWinner(null);
    setWinLine(null);
    setPhase('lobby');
  }

  function handleExitToHome() {
    // Mid-match back-press opens the quit-confirm so the user has
    // one deliberate chance to bail out instead of forfeiting the
    // bet on an accidental tap. Queue/VS are treated as "mid-match"
    // too because a bet has already been taken at queue-join.
    if (phase === 'playing' || phase === 'between' || phase === 'vs') {
      setQuitConfirm(true);
      return;
    }
    if (phase === 'queue') {
      // Queue cancel: refund is handled server-side, go straight home.
      wsRef.current?.send({ type: 'xo_leave_game' });
    }
    nav('/');
  }

  function handleQuit() {
    // Confirmed mid-match forfeit. Server kicks us with reason='manual'
    // which awards the match to the opponent and writes our 'loss'
    // recovery row.
    wsRef.current?.send({ type: 'xo_leave_game' });
    setQuitConfirm(false);
    nav('/');
  }

  function acknowledgeRecovery() {
    if (!recovery) return;
    const sid = recovery.sessionId;
    // See core/lib/recoveryAck: marks the session dismissed locally
    // first, then retries the server POST. Guarantees the popup never
    // reappears on this device even if the network call drops.
    markSessionAcked('xo', sid);
    api.post('/xo/acknowledge-result', { sessionId: sid })
      .then(() => clearSessionAcked('xo', sid))
      .catch(() => {});
    setRecovery(null);
  }

  // ─── Render ───────────────────────────────────────────────────────
  // NOTE: `ludo-root` + `app-container` class names are reused so both
  // games share the safe-area handling / fixed-panel sizing. The theme
  // override lives under `.xo-root .app-container` in xo.css.
  return (
    <div className="ludo-root xo-root fixed inset-0 z-[9999]">
      {/* Loading overlay — blocks the lobby until BOTH the WS auth has
          landed AND every preload URL has settled. See useAssetPreloader
          for the per-URL semantics. */}
      <GameLoadingScreen
        visible={!authReady || !assetStatus.ready}
        theme="xo"
        progress={assetStatus.progress}
      />
      <div className="app-container shadow-2xl">
        {/* Shared settings modal — platform-wide component originally
            built for Rummy; per user request every game uses the same
            menu so the UX is identical everywhere. */}
        <GameSettingsMenu
          open={settingsOpen}
          soundOn={soundEnabled}
          micMuted={micMuted}
          listenMuted={listenMuted}
          voiceEnabled={phase === 'playing' || phase === 'between' || phase === 'vs'}
          onToggleSound={() => { playTap(); setSoundEnabled((s) => !s); }}
          onToggleMic={() => {
            playTap();
            setMicMuted((v) => {
              const next = !v;
              voiceRef.current?.setMuted(next);
              return next;
            });
          }}
          onToggleListen={() => {
            playTap();
            setListenMuted((v) => {
              const next = !v;
              voiceRef.current?.setListenMuted(next);
              if (!next) voiceRef.current?.kickAudioPlayback?.();
              return next;
            });
          }}
          onExit={() => {
            playTap();
            setSettingsOpen(false);
            handleExitToHome();
          }}
          onClose={() => setSettingsOpen(false)}
          exitLabel={(phase === 'playing' || phase === 'between' || phase === 'vs') ? 'Exit Game' : 'Exit to Home'}
          anchor="left"
        />

        <ConcurrentMatchModal
          open={concOpen}
          blocking={concBlocking}
          walletBalance={wallet}
          onResolved={() => {
            setConcOpen(false);
            setConcBlocking(null);
            toast.push('success', 'Active matches resolved — try again', 2000);
          }}
          onCancel={() => setConcOpen(false)}
        />

        <XoTopBar
          walletAmt={wallet}
          matchPayout={phase === 'playing' || phase === 'between' ? matchPayout : null}
          onBack={() => { playTap(); handleExitToHome(); }}
          onSettings={() => { playTap(); setSettingsOpen((o) => !o); }}
        />

        {phase === 'lobby' && (
          <LobbyBanner
            enabled={lobbyBanner.enabled}
            text={lobbyBanner.text}
            tone={lobbyBanner.tone}
          />
        )}
        {phase === 'lobby' && (
          <XoSetupScreen
            onPick={(b) => { playTap(); startMatch(b); }}
            waitingByBet={waitingByBet}
            showWaitingBadge={showWaitingBadge}
            online={online}
            betTiers={betTiers}
            platformFeePercent={platformFeePercent}
          />
        )}

        {phase === 'queue' && (
          <XoWaitingModal
            youName={youName}
            yourAvatar={user?.profile_image_url || null}
            profiles={queueProfiles.profiles}
            yourUserId={queueProfiles.yourUserId}
            onCancel={handleCancelQueue}
          />
        )}

        {phase === 'vs' && yourMark && (
          <XoVsScreen
            yourMark={yourMark}
            youName={youName}
            oppName={oppName}
            pot={matchPot}
            payout={matchPayout}
            yourAvatarUrl={profiles[yourMark]?.avatarUrl ?? null}
            oppAvatarUrl={oppMark ? (profiles[oppMark]?.avatarUrl ?? null) : null}
          />
        )}

        {(phase === 'playing' || phase === 'between') && yourMark && (
          <XoGameScreen
            cells={cells}
            currentTurn={currentTurn}
            yourMark={yourMark}
            youName={youName}
            oppName={oppName}
            round={round}
            score={score}
            strikes={strikes}
            winLine={winLine}
            winner={lastRoundWinner === 'draw' ? null : (lastRoundWinner as Mark | null)}
            betweenSplash={phase === 'between' ? lastRoundWinner : null}
            onCellClick={handleCellClick}
            turnSeq={turnSeq}
            absentMark={absent}
            timerDrainSec={Math.max(1, turnSeconds - clientTimerOffsetSeconds)}
            yourAvatarUrl={profiles[yourMark]?.avatarUrl ?? null}
            oppAvatarUrl={oppMark ? (profiles[oppMark]?.avatarUrl ?? null) : null}
          />
        )}

        {phase === 'result' && matchOutcome && yourMark && (
          <XoResultScreen
            outcome={matchOutcome}
            yourMark={yourMark}
            score={score}
            payout={matchPayout}
            pot={matchPot}
            onNewMatch={handleBackToLobby}
            onHome={() => nav('/')}
          />
        )}

        {/* Recovery popup — "Previous game: YOU WONX" etc., shown
            only in the lobby and only if /pending-result returned
            something unacknowledged. */}
        {recovery && phase === 'lobby' && (
          <XoRecoveryScreen
            result={recovery.result}
            payout={Number(recovery.payout || 0) | 0}
            pot={Number(recovery.pot || 0) | 0}
            yourRounds={Number(recovery.yourRounds || 0) | 0}
            oppRounds={Number(recovery.oppRounds || 0) | 0}
            opponents={recovery.opponents || []}
            onAck={acknowledgeRecovery}
          />
        )}

        {/* Mid-match quit confirm. Shown when the user presses Back
            or Exit during playing / between / VS phases so an
            accidental tap can't forfeit their bet. */}
        {quitConfirm && (
          <XoQuitConfirm
            amount={selectedBet || matchPot / 2}
            onStay={() => setQuitConfirm(false)}
            onQuit={handleQuit}
          />
        )}

        {/* Shared "not enough balance" pop. */}
        <InsufficientFundsModal
          open={insufficientOpen}
          required={insufficientBet}
          balance={wallet}
          onClose={() => setInsufficientOpen(false)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top bar
// ─────────────────────────────────────────────────────────────────────
function XoTopBar({
  walletAmt, matchPayout, onBack, onSettings,
}: { walletAmt: number; matchPayout: number | null; onBack: () => void; onSettings: () => void }) {
  return (
    <div
      className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#022c22] to-[#0f3b3a] shadow-md z-[150] w-full"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      {/* Left cluster: [Back] [Settings]. Settings uses the same
          Settings.png asset as Ludo (copied into /games/xo/) so the
          two games feel like siblings; back button keeps the
          Material Symbols arrow because there's no "back" asset. */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/40 border border-white/15 hover:bg-black/60 active:scale-95 transition text-white"
        >
          <Icon name="arrow_back" size={22} filled />
        </button>
        <button
          onClick={onSettings}
          aria-label="Settings"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
        >
          <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
        </button>
      </div>

      {matchPayout != null && (
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="bg-gradient-to-b from-[#0a3a38] to-[#0f3b3a] rounded-lg flex items-center justify-center px-3 py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)]">
            {/* Cash tier-2 image used as the pot chip artwork, mirroring
                Ludo's in-play TopBar chip exactly — swap for an X&O
                specific cash graphic later. */}
            <img src={IMG.cash[1]} alt="" className="w-6 h-6 sm:w-7 sm:h-7 object-contain mr-2 relative scale-110" />
            <div className="flex flex-col items-start leading-none mr-1 sm:mr-2">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-white/70">WIN</span>
              <span className="text-white font-bold text-base sm:text-lg tracking-wide tabular-nums mt-0.5">
                {fmt(matchPayout)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-b from-[#0a2e2c] to-[#0f3b3a] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)]">
        <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
        <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide tabular-nums">
          {fmt(walletAmt)}
        </span>
        <button className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform">
          <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
        </button>
      </div>
    </div>
  );
}

// NOTE: the old `XoSettingsMenu` component used to live here. It has
// been replaced by the shared `core/components/GameSettingsMenu` so
// every game shares one consistent settings UX. State + handlers are
// still managed by this Page; the shared component just renders them.

// ─────────────────────────────────────────────────────────────────────
// Lobby / bet selection
// ─────────────────────────────────────────────────────────────────────
function XoSetupScreen({
  onPick, waitingByBet, showWaitingBadge, online,
  betTiers, platformFeePercent,
}: {
  onPick: (bet: number) => void;
  waitingByBet: Record<string, number>;
  showWaitingBadge: boolean;
  // Server-driven online count — see xo_global_stats on the parent.
  // The backend walks a base number up and down with a drift tick
  // (while BOT_ENABLED is true), so the lobby feels populated even
  // during low-traffic windows. Matches Ludo's pattern exactly.
  online: number;
  // Admin-editable server config passed down from the parent — live
  // values from GET /api/xo/config, NOT hard-coded constants. Changes
  // to the DB flow through here automatically after a page refresh.
  betTiers: number[];
  platformFeePercent: number;
}) {

  // Default selected bet = first tier. Matches Ludo's behaviour where
  // a card is always visibly selected, giving the "pick another to
  // change your mind" feedback. Clicking a different card immediately
  // starts matchmaking (no separate "Play" button).
  const [selectedBet, setSelectedBet] = useState<number>(betTiers[0] ?? 0);

  return (
    <div
      id="xo-setup-screen"
      className="flex flex-col w-full h-full flex-grow goldern_border rounded-none sm:rounded-b-3xl relative"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div id="xo-lobby-content" className="flex flex-col flex-grow w-full">
        {/* Top section — BIG logo (Ludo parity: maxHeight 250px,
            flex-grow centered). No secondary text below — the logo
            itself is the game's identity. Online pill beneath. */}
        <div className="flex-grow flex flex-col justify-center items-center px-4 sm:px-6 min-h-[150px]">
          <img
            src={IMG.logo}
            className="w-full h-full object-contain drop-shadow-2xl"
            style={{ maxHeight: 250 }}
            alt=""
          />
          <div className="mt-4 text-[#a7f3d0] text-[10px] sm:text-xs font-bold tracking-widest bg-black/50 px-4 py-2 rounded-full border border-[#14b8a6] flex items-center justify-center gap-2">
            <span
              className="inline-block w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.8)]"
              aria-hidden
            />
            <span>{online} PLAYERS ONLINE</span>
          </div>
        </div>

        {/* Bottom section — Best-of-3 chip + cash grid. Tight to the
            bottom-safe-area padding so the logo dominates the screen. */}
        <div className="px-4 sm:px-5 mt-auto flex flex-col gap-3 w-full pb-6">
          {/* Original chip+pill style — just slightly taller + slightly
              bolder text so it reads clearly at a glance without the
              loud marquee treatment that didn't land. */}
          <div className="xo-bestof-chip">
            <span>Match Format</span>
            <span className="xo-bestof-pill">Best of 3</span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {betTiers.map((val, i) => {
              const winAmt = Math.floor(val * 2 * (100 - platformFeePercent) / 100);
              const isSelected = selectedBet === val;
              // JSON keys come back as strings.
              const waiting = Number(waitingByBet?.[String(val)] || 0);
              const showBadge = showWaitingBadge && waiting > 0;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => { setSelectedBet(val); onPick(val); }}
                  className={['xo-cash-btn', isSelected ? 'selected' : ''].join(' ')}
                >
                  {showBadge && <XoWaitingBadge count={waiting} />}
                  <div className="flex flex-col items-start justify-center pointer-events-none">
                    <span className={'xo-cash-amt' + (val === 0 ? ' xo-cash-amt--free' : '')}>
                      {val === 0 ? 'FREE' : val}
                    </span>
                    <span className="xo-cash-win">
                      {val === 0 ? 'PLAY FOR FUN' : `WIN ${winAmt}`}
                    </span>
                  </div>
                  <img
                    src={IMG.cash[i] || IMG.cash[IMG.cash.length - 1]}
                    className="xo-cash-img pointer-events-none"
                    alt=""
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Small "N waiting" pill shown on the top-right of a cash tile when
// real players are currently queued at that stake. `count` comes from
// the server's humans-only rollup — bots are never included.
function XoWaitingBadge({ count }: { count: number }) {
  return (
    <span
      className="xo-cash-waiting-badge"
      aria-label={`${count} waiting`}
      title={`${count} waiting at this stake`}
    >
      <svg
        width="10" height="10" viewBox="0 0 24 24"
        fill="currentColor" aria-hidden="true"
        className="xo-cash-waiting-badge__icon"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
      <span className="xo-cash-waiting-badge__num">{count}</span>
      <span className="xo-cash-waiting-badge__label">waiting</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Waiting modal
// ─────────────────────────────────────────────────────────────────────
function XoWaitingModal({
  youName, yourAvatar, profiles, yourUserId, onCancel,
}: {
  youName: string;
  yourAvatar: string | null;
  profiles: Array<{ userId?: string; name?: string; displayName?: string; avatarUrl?: string | null; isBot?: boolean; is_bot?: boolean }>;
  yourUserId: string | null;
  onCancel: () => void;
}) {
  const [dots, setDots] = useState(0);
  const [flipIdx, setFlipIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d + 1) % 3), 400);
    const f = setInterval(() => setFlipIdx((i) => (i + 1) % 4), 450);
    return () => { clearInterval(t); clearInterval(f); };
  }, []);
  const dotsText = dots === 0 ? '.' : dots === 1 ? '..' : '...';
  const searchAvatars = [
    '/games/xo/RC_Player_red.png',
    '/games/xo/RC_Player_green.png',
    '/games/xo/RC_Player_yellow.png',
    '/games/xo/RC_Player_blue.png',
  ];
  // Pick the first profile that ISN'T the local player. Server pushes
  // an updated profiles array on every queue change, so the moment an
  // opponent (human or bot) is seated they show up here. While the
  // queue is still 1/2 we render the cycling-search placeholder.
  const opponent = (profiles || []).find((p) => p && String(p.userId || '') !== String(yourUserId || ''));
  const oppName = opponent ? (opponent.name || opponent.displayName || 'Opponent') : null;
  const oppAvatar = opponent?.avatarUrl || null;
  const youProfile = (profiles || []).find((p) => p && String(p.userId || '') === String(yourUserId || ''));
  const yourAvatarFinal = yourAvatar || youProfile?.avatarUrl || null;
  return (
    <div className="absolute inset-0 z-[160] bg-black/85 flex justify-center items-center">
      <div className="relative w-[85%] max-w-[340px] border-[3px] border-[#d4af37] bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#0f3b3a] via-[#042f2e] to-[#020617] rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.9)] flex flex-col items-center gap-4">
        {/* Top-right × dismiss. Ludo parity — every dismissible modal
            on the platform has this red-gradient circular close. */}
        <button
          onClick={onCancel}
          aria-label="Cancel"
          className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform z-10"
        >
          ✕
        </button>

        <img src={IMG.logo} className="w-24 h-auto object-contain drop-shadow-2xl mb-1" alt="" />

        <h2 className="text-center">
          <span className="block text-xs tracking-[0.3em] text-[#a7f3d0] uppercase">Finding</span>
          <span className="block text-5xl font-black leading-none my-1 text-[#dfff7a] drop-shadow-[0_0_14px_rgba(223,255,122,0.45)]">
            Opponent
          </span>
        </h2>

        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1.5" style={{ width: 78 }}>
            <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-[#22d3ee] shadow-[0_0_14px_rgba(34,211,238,0.65)]">
              <img src={yourAvatarFinal || IMG.playerX} className="w-full h-full object-cover" alt="" />
            </div>
            <div className="text-[10px] font-black tracking-widest text-white truncate max-w-full text-center">
              {(youName || 'YOU').toUpperCase()}
            </div>
          </div>

          <div className="xo-wait-vs">V/S</div>

          <div className="flex flex-col items-center gap-1.5" style={{ width: 78 }}>
            {/* Opponent slot — real avatar+name once the server tells us
                someone joined the queue, otherwise the cycling search
                placeholder. The real-data branch shares the solid
                rose ring with no animation; the placeholder branch
                keeps the dashed border + search-icon pulse + dots. */}
            {opponent ? (
              <>
                <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-[#fb7185] shadow-[0_0_14px_rgba(251,113,133,0.55)]">
                  <img src={oppAvatar || searchAvatars[0]} className="w-full h-full object-cover" alt="" />
                </div>
                <div className="text-[10px] font-black tracking-widest text-[#fda4af] truncate max-w-full text-center">
                  {(oppName || 'OPPONENT').toUpperCase()}
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full overflow-hidden border-[3px] border-dashed border-[#fb7185] bg-black/30 relative xo-wait-search">
                  <img src={searchAvatars[flipIdx]} className="w-full h-full object-cover opacity-90" alt="" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Icon name="search" size={26} filled className="text-white/95 animate-pulse" />
                  </div>
                </div>
                <div className="text-[10px] font-black tracking-widest text-[#fda4af]">{dotsText}</div>
              </>
            )}
          </div>
        </div>

        <div className="w-full bg-[#042f2e] border border-[#14b8a6] rounded-lg py-2 px-3 shadow-inner flex justify-around items-center">
          <span className="text-[#a7f3d0] text-[10px] tracking-widest">SEARCHING</span>
          <span className="text-[#dfff7a] text-[10px] tracking-widest font-black">ACROSS THE BOARD</span>
        </div>

        {/* Bottom full-width CANCEL — Ludo parity. The × in the corner
            is the quick-dismiss; this is the primary, thumb-friendly
            cancel action at the bottom of the modal. */}
        <button
          onClick={onCancel}
          className="w-full py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest text-shadow-heavy shadow-md hover:brightness-110 active:scale-[0.98] transition-all"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VS screen
// ─────────────────────────────────────────────────────────────────────
function XoVsScreen({
  yourMark, youName, oppName, pot, payout, yourAvatarUrl, oppAvatarUrl,
}: {
  yourMark: Mark; youName: string; oppName: string; pot: number; payout: number;
  yourAvatarUrl?: string | null;
  oppAvatarUrl?: string | null;
}) {
  const oppMark: Mark = yourMark === 'x' ? 'o' : 'x';
  return (
    <div className="absolute inset-0 z-[180] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#0f3b3a] to-[#020617] flex flex-col items-center justify-center gap-6 p-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{
             background: 'radial-gradient(circle at 30% 30%, rgba(34,211,238,0.25), transparent 40%), radial-gradient(circle at 70% 70%, rgba(251,113,133,0.25), transparent 40%)',
           }} />

      <div className="text-[10px] uppercase tracking-[0.4em] text-[#a7f3d0]">MATCH FOUND</div>

      <div className="flex items-center gap-5">
        <XoVsCard name={youName} mark={yourMark} isMe avatarUrl={yourAvatarUrl ?? null} />
        <div className="text-4xl font-black text-[#fde08b] drop-shadow">V / S</div>
        <XoVsCard name={oppName} mark={oppMark} avatarUrl={oppAvatarUrl ?? null} />
      </div>

      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a7f3d0]">POT</div>
        <div className="text-2xl font-black text-[#fde08b]">{fmt(pot)}</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a7f3d0] mt-1">WIN{fmt(payout)}</div>
      </div>
    </div>
  );
}

function XoVsCard({ name, mark, isMe, avatarUrl }: { name: string; mark: Mark; isMe?: boolean; avatarUrl?: string | null }) {
  // Real avatar image inside the card with an X or O mark-badge
  // overlaid in the corner. Lifts the weight from a text glyph into
  // a proper player card (mirrors Ludo's VS cards).
  //
  // Pre-fix this hardcoded the per-mark default cartoons regardless
  // of who was actually seated, which is the root cause of "the photo
  // disappears after refresh" — the photo was never used. Now we
  // prefer the player's real avatarUrl (loaded into `profiles[mark]`
  // from xo_match_start / xo_reconnect_sync) and fall back to the
  // mark-themed cartoon only when the seat has no uploaded image.
  // Same pattern Ludo / Rummy / Chess / Carrom HUDs already use.
  const avatar = avatarUrl || (mark === 'x' ? IMG.playerX : IMG.playerO);
  const color = mark === 'x' ? 'var(--xo-x)' : 'var(--xo-o)';
  const glow = mark === 'x' ? 'rgba(34,211,238,0.55)' : 'rgba(251,113,133,0.55)';
  return (
    <div className={`flex flex-col items-center gap-2 ${isMe ? 'scale-110' : ''}`}>
      {isMe && (
        <div className="bg-[#fde08b] text-black px-2 py-0.5 rounded text-[9px] font-black tracking-widest">
          YOU
        </div>
      )}
      <div className="relative">
        <div
          className="w-20 h-20 rounded-full overflow-hidden"
          style={{
            border: `3px solid ${color}`,
            boxShadow: `0 0 18px ${glow}`,
          }}
        >
          <img src={avatar} className="w-full h-full object-cover" alt="" />
        </div>
        {/* Mark badge — small pill at bottom-centre showing X or O via
            the shared SVG Mark so it renders with identical stroke
            weight everywhere. */}
        <div
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full grid place-items-center"
          style={{
            background: '#042f2e',
            border: `2px solid ${color}`,
            boxShadow: `0 4px 10px rgba(0,0,0,0.6), 0 0 10px ${glow}`,
          }}
        >
          <Mark kind={mark} size={20} glow={false} />
        </div>
      </div>
      <div className="text-xs font-black tracking-widest uppercase text-white mt-2">{name}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Game screen
// ─────────────────────────────────────────────────────────────────────
function XoGameScreen({
  cells, currentTurn, yourMark, youName, oppName, round, score, strikes,
  winLine, winner, betweenSplash, onCellClick, turnSeq, absentMark,
  timerDrainSec, yourAvatarUrl, oppAvatarUrl,
}: {
  cells: Cell[]; currentTurn: Mark; yourMark: Mark;
  youName: string; oppName: string; round: number;
  score: { x: number; o: number; draws: number };
  strikes: { x: number; o: number };
  winLine: number[] | null;
  winner: Mark | null;
  betweenSplash: Mark | 'draw' | null;
  onCellClick: (i: number) => void;
  turnSeq: number;
  // Non-null while one mark is inside the disconnect-grace window.
  // When set we grey that player's row, hide the turn-indicator glow,
  // and park the timer bar — the server has paused the turn clock too.
  absentMark: Mark | null;
  // Admin-tunable drain duration for the visible turn bar. Derived in
  // the parent from `turnSeconds - clientTimerOffsetSeconds`.
  timerDrainSec: number;
  // Profile avatar URLs for the two seats — passed in from the parent
  // so XoPlayerRow can render the player's actual photo instead of
  // the per-mark cartoon default. Pre-fix the in-match HUD hardcoded
  // IMG.playerX / IMG.playerO regardless of who was seated.
  yourAvatarUrl?: string | null;
  oppAvatarUrl?: string | null;
}) {
  const oppMark: Mark = yourMark === 'x' ? 'o' : 'x';
  const yourTurn = currentTurn === yourMark;
  const yourScore = score[yourMark];
  const oppScore = score[oppMark];
  return (
    <div
      id="xo-game-screen"
      className="flex flex-col w-full h-full flex-grow goldern_border rounded-none sm:rounded-b-3xl relative"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="xo-game-wrap">
        {/* Round + score strip */}
        <div className="xo-round-strip">
          <div>Round {round} / {MAX_ROUNDS}</div>
          <div className="xo-score">
            <span className="xo-score-you">{yourScore}</span>
            <span className="xo-score-sep">•</span>
            <span className="xo-score-opp">{oppScore}</span>
          </div>
        </div>

        {/* Opponent profile — top. Timer bar sits BELOW the row when
            it's the top player, so the "draining" edge faces the
            board — exact parity with Ludo's top-player panels. */}
        <div className={`xo-player-wrap ${absentMark === oppMark ? 'xo-player-wrap--absent' : ''}`}>
          <XoPlayerRow
            name={oppName}
            sub={absentMark === oppMark ? 'Away…' : 'Opponent'}
            mark={oppMark}
            isTurn={!yourTurn && !betweenSplash && !absentMark}
            score={score[oppMark]}
            strikes={strikes[oppMark]}
            avatarUrl={oppAvatarUrl ?? null}
          />
          <div className="xo-timer-container">
            {!yourTurn && !betweenSplash && !absentMark
              ? <XoTimerFill key={`opp-${turnSeq}`} drainSec={timerDrainSec} />
              : <div className="xo-timer-fill" />}
          </div>
        </div>

        {/* Board + between-rounds splash overlay */}
        <div className="xo-board-wrap relative">
          <Board
            cells={cells}
            canClick={yourTurn && !betweenSplash}
            disabledCells={betweenSplash ? Array(9).fill(true) : undefined}
            winLine={winLine}
            winner={winner}
            onCellClick={onCellClick}
          />
          {betweenSplash && (
            <div className="xo-splash">
              <div className="xo-splash-title">
                {betweenSplash === 'draw'
                  ? 'Round Drawn'
                  : betweenSplash === yourMark
                    ? 'You Won'
                    : 'Opponent Won'}
              </div>
              <div className="xo-splash-sub">Round {round} of {MAX_ROUNDS}</div>
            </div>
          )}
        </div>

        {/* Self profile — bottom. Timer above it (facing the board). */}
        <div className="xo-player-wrap">
          <div className="xo-timer-container">
            {yourTurn && !betweenSplash && !absentMark
              ? <XoTimerFill key={`you-${turnSeq}`} drainSec={timerDrainSec} />
              : <div className="xo-timer-fill" />}
          </div>
          <XoPlayerRow
            name={youName}
            sub="You"
            mark={yourMark}
            isTurn={yourTurn && !betweenSplash && !absentMark}
            score={score[yourMark]}
            strikes={strikes[yourMark]}
            avatarUrl={yourAvatarUrl ?? null}
          />
        </div>
      </div>
    </div>
  );
}

// Animated timer bar — on mount, fills to 100% then drains to 0 over
// `drainSec`. The parent uses `key={turnSeq}` so a fresh bar
// mounts at every turn change. Colour drifts from white → coral
// as time runs low, matching Ludo's TimerFill behaviour.
//
// `drainSec` is admin-controlled: it's `turnSeconds - clientTimerOffsetSeconds`
// so the visible bar hits 0 a small safety-beat BEFORE the server's
// authoritative turn timeout. Previously hard-coded to 15; now
// passed as a prop so admin edits apply without redeploy.
function XoTimerFill({ drainSec }: { drainSec: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.width = '100%';
    el.style.backgroundColor = '#ffffff';
    el.style.transition = 'none';
    void el.offsetWidth;
    const s = Math.max(1, drainSec);
    el.style.transition = `width ${s}s linear, background-color ${s}s linear`;
    el.style.width = '0%';
    el.style.backgroundColor = '#fb7185';
  }, [drainSec]);
  return <div ref={ref} className="xo-timer-fill" />;
}

function XoPlayerRow({
  name, sub, mark, isTurn, score, strikes, avatarUrl,
}: { name: string; sub: string; mark: Mark; isTurn: boolean; score: number; strikes: number; avatarUrl?: string | null }) {
  return (
    <div className={`xo-player ${mark === 'x' ? 'is-x' : 'is-o'} ${isTurn ? 'is-turn' : ''}`}>
      <div className="xo-player-avatar">
        {/* Prefer the player's real avatarUrl (loaded into profiles[mark]
            from xo_match_start / xo_reconnect_sync). Fall back to the
            mark-themed cartoon only when there's no uploaded image. */}
        <img
          src={avatarUrl || (mark === 'x' ? IMG.playerX : IMG.playerO)}
          alt=""
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="xo-player-name">{name}</div>
        <div className="xo-player-sub">{sub}{isTurn ? ' · Your turn' : ''}</div>
        {/* Strike dots — one red pip per turn-timeout this match, so
            the player can see timeout risk ramping. Match kick limit
            is 3 (XoGame.HUMAN_STRIKE_LIMIT). */}
        <div className="xo-strike-dots" aria-label={`${strikes} strikes`}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={['xo-strike-dot', i < strikes ? 'on' : ''].join(' ')}
            />
          ))}
        </div>
      </div>
      {/* Round-wins pips — two dots, filled in the player's mark
          colour for each round already won (best-of-3 = first to 2). */}
      <div className="xo-score-dots" aria-label={`${score} rounds won`}>
        {[0, 1].map((i) => (
          <span
            key={i}
            className={[
              'xo-score-dot',
              mark === 'x' ? 'x' : 'o',
              i < score ? 'on' : '',
            ].join(' ')}
          />
        ))}
      </div>
      <div className={`xo-player-mark ${mark}`}>
        <Mark kind={mark} size={24} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Result screen
// ─────────────────────────────────────────────────────────────────────
function XoResultScreen({
  outcome, yourMark, score, payout, pot, onNewMatch, onHome,
}: {
  outcome: 'win' | 'loss' | 'draw'; yourMark: Mark;
  score: { x: number; o: number; draws: number };
  payout: number; pot: number;
  onNewMatch: () => void; onHome: () => void;
}) {
  const oppMark: Mark = yourMark === 'x' ? 'o' : 'x';
  const you = score[yourMark];
  const opp = score[oppMark];
  const title =
    outcome === 'win' ? 'YOU WON!'
    : outcome === 'loss' ? 'YOU LOST'
    : 'DRAW';
  const amountLabel =
    outcome === 'win' ? 'Credited'
    : outcome === 'loss' ? 'Bet Forfeited'
    : 'Bet Refunded';
  const amount = outcome === 'win' ? payout : outcome === 'loss' ? pot / 2 : pot / 2;
  // Use the same trophy / defeat illustrations Ludo uses for now
  // (copied into /games/xo/). Draws fall back to the platform coin
  // asset as a neutral "money returned" visual. Material Symbols
  // stays the fallback in case the image fails to load.
  const resultImg =
    outcome === 'win' ? IMG.trophy
    : outcome === 'loss' ? IMG.defeat
    : IMG.coin;
  const iconColor =
    outcome === 'win' ? 'text-[#fde08b]'
    : outcome === 'loss' ? 'text-[#f87171]'
    : 'text-[#a7f3d0]';

  return (
    <div className="absolute inset-0 z-[190] bg-black/80 flex items-center justify-center p-5">
      <div className="relative w-full max-w-[340px] rounded-2xl bg-gradient-to-b from-[#042f2e] via-[#031a1a] to-[#020617] border-[3px] border-[#14b8a6] shadow-[0_10px_40px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-4">
        <img
          src={resultImg}
          alt=""
          className={`w-[90px] h-[90px] object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.6)] ${iconColor}`}
        />
        <h1 className="text-3xl font-black tracking-widest text-white">{title}</h1>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a7f3d0]">
          {you} round{you === 1 ? '' : 's'} to {opp}
        </div>
        <div className="w-full rounded-xl bg-black/40 border border-[#14b8a6] px-4 py-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-[#a7f3d0]">{amountLabel}</span>
          <span className="text-xl font-black text-[#fde08b] tabular-nums">{fmt(amount)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          <button className="xo-primary-btn !bg-gradient-to-b !from-[#1f2937] !to-[#0f172a] !border-[#374151] !text-white" onClick={onHome}>
            Home
          </button>
          <button className="xo-primary-btn" onClick={onNewMatch}>
            New Match
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Quit confirm — shown when the user presses Back mid-match so an
// accidental tap can't forfeit the bet.
// ─────────────────────────────────────────────────────────────────────
function XoQuitConfirm({
  amount, onStay, onQuit,
}: { amount: number; onStay: () => void; onQuit: () => void }) {
  return (
    <div className="absolute inset-0 z-[400] bg-black/75 flex items-center justify-center p-4">
      <div className="relative w-full max-w-[340px] bg-gradient-to-b from-[#0f3b3a] via-[#042f2e] to-[#020617] rounded-2xl border-[3px] border-[#d4af37] shadow-[0_20px_60px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-3">
        <button
          type="button"
          aria-label="Close"
          onClick={onStay}
          className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform z-10"
        >
          ✕
        </button>
        <img src={IMG.quitWarn} className="w-16 h-16 object-contain drop-shadow" alt="" />
        <h2 className="text-2xl font-black text-white tracking-widest text-center">Forfeit the match?</h2>
        <p className="text-center text-[13px] text-[#dfff7a] max-w-[280px]">
          Leaving now ends the game for you and your bet of
          <span className="text-[#fde08b] font-black">{fmt(amount)} </span>
          will be forfeited.
        </p>
        <div className="grid grid-cols-2 gap-2 w-full mt-1">
          <button
            onClick={onStay}
            className="py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#10b981] to-[#047857] text-white font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition"
          >
            STAY
          </button>
          <button
            onClick={onQuit}
            className="py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition"
          >
            FORFEIT
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Recovery screen — "PREVIOUS GAME · YOU WONX" etc. Shown once from
// /api/xo/pending-result when the user returns to the lobby with an
// unacknowledged result. Clicking "Back to Lobby" fires the HTTP
// acknowledge so the same popup doesn't re-surface on refresh.
// ─────────────────────────────────────────────────────────────────────
function XoRecoveryScreen({
  result, payout, pot, yourRounds, oppRounds, opponents, onAck,
}: {
  result: 'win' | 'loss' | 'kick' | 'draw';
  payout: number;
  pot: number;
  yourRounds: number;
  oppRounds: number;
  opponents: Array<{ name?: string; mark?: string; is_bot?: boolean }>;
  onAck: () => void;
}) {
  // Label + outcome split into two lines so narrow devices never wrap
  // the title mid-word. Matches Ludo's RecoveryScreen layout.
  const titleLabel = 'PREVIOUS GAME';
  const title =
    result === 'win'  ? 'YOU WON!'
    : result === 'kick' ? 'DISCONNECTED'
    : result === 'draw' ? 'DRAW'
    : 'YOU LOST';
  const resultImg =
    result === 'win' ? IMG.trophy
    : result === 'loss' || result === 'kick' ? IMG.defeat
    : IMG.coin;
  const amountLabel =
    result === 'win' ? 'Credited'
    : result === 'draw' ? 'Refunded'
    : 'Bet Forfeited';
  const amount = result === 'win' ? payout : result === 'draw' ? Math.floor(pot / 2) : pot;
  const oppNames = (opponents || []).map((o) => o?.name).filter(Boolean).join(', ');
  const subtitle = oppNames
    ? `Played vs ${oppNames}`
    : result === 'win' ? 'Glory was yours'
    : result === 'draw' ? 'A tight battle'
    : 'Better luck next round';

  return (
    <div className="absolute inset-0 z-[350] bg-black/80 flex items-center justify-center p-5">
      <div className="relative w-full max-w-[340px] rounded-2xl bg-gradient-to-b from-[#042f2e] via-[#031a1a] to-[#020617] border-[3px] border-[#14b8a6] shadow-[0_10px_40px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-4">
        <img src={resultImg} alt="" className="w-[80px] h-[80px] object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.6)]" />
        <div className="flex flex-col items-center gap-0.5 -mt-2">
          <div className="text-[10px] font-bold tracking-[0.32em] text-[#a7f3d0]/80 uppercase">
            {titleLabel}
          </div>
          <h1 className="text-lg font-black tracking-widest text-white text-center leading-tight">
            {title}
          </h1>
        </div>
        <p className="text-[11px] tracking-widest text-[#a7f3d0] uppercase">{subtitle}</p>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a7f3d0]">
          {yourRounds} round{yourRounds === 1 ? '' : 's'} to {oppRounds}
        </div>
        <div className="w-full rounded-xl bg-black/40 border border-[#14b8a6] px-4 py-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-[#a7f3d0]">{amountLabel}</span>
          <span className="text-xl font-black text-[#fde08b] tabular-nums">{fmt(amount)}</span>
        </div>
        <button
          onClick={onAck}
          className="w-full py-3 rounded-lg border-2 border-[#064e3b] bg-gradient-to-b from-[#10b981] to-[#047857] text-white font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition"
        >
          BACK TO LOBBY
        </button>
      </div>
    </div>
  );
}
